// Persistenz für „Meine Dokumente" — Firebase Storage + Firestore.
// Pfad-Schema:  users/{uid}/dokumente/{kategorie}/{ts}-{filename}
// Metadata-Doc: dokumente/{docId} mit { uid, kategorie, filename, size, type, storagePath, url, uploadedAt }

import {
  collection, query, where, orderBy, onSnapshot, getDocs,
  deleteDoc, doc, updateDoc, setDoc, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import {
  ref, deleteObject,
} from 'firebase/storage';
import { db, storage, auth } from './firebase';
import { BASE_URL as API_URL } from './api';

async function _idToken() {
  const user = auth.currentUser;
  if (!user) throw new Error('Nicht angemeldet');
  return user.getIdToken();
}

const COL = 'dokumente';
const DOKUMENTE_SIGNAL = 'cvflow:dokumente:changed';
const DOKUMENTE_CHANNEL = 'cvflow-dokumente';

const dokumenteSubscribers = new Set();
let dokumenteCache = null;
let dokumenteCacheUid = null;
let dokumenteLoadPromise = null;
let dokumenteLoadUid = null;
let dokumenteLoadToken = 0;
let dokumenteReloadTimer = null;
let dokumenteChannel = null;
let dokumenteSignalsReady = false;

function uid() {
  const u = auth.currentUser;
  if (!u) throw new Error('Nicht angemeldet');
  return u.uid;
}

function sanitize(name) {
  return name.replace(/[^\w.\-äöüÄÖÜß ]/g, '_');
}

function currentTabId() {
  if (typeof window === 'undefined') return 'server';
  if (!window.__cvflowDokumenteTabId) {
    window.__cvflowDokumenteTabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  return window.__cvflowDokumenteTabId;
}

function emitDokumente(docs) {
  dokumenteSubscribers.forEach(({ onChange }) => {
    try { onChange(docs); } catch { /* subscriber errors must not break other listeners */ }
  });
}

function emitDokumenteError(err) {
  dokumenteSubscribers.forEach(({ onError }) => {
    try { (onError || (() => {}))(err); } catch { /* ignore */ }
  });
}

export async function loadDokumente() {
  const u = uid();
  const q = query(
    collection(db, COL),
    where('uid', '==', u),
    orderBy('uploadedAt', 'desc'),
  );
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  console.log('[Firestore] Dokumente einmalig geladen:', docs.length);
  return docs;
}

function ensureDokumenteLoaded({ force = false } = {}) {
  const u = uid();
  if (!force && dokumenteCache && dokumenteCacheUid === u) {
    emitDokumente(dokumenteCache);
    return Promise.resolve(dokumenteCache);
  }
  if (dokumenteLoadPromise && dokumenteLoadUid === u) {
    if (!force) return dokumenteLoadPromise;
    return dokumenteLoadPromise
      .catch(() => {})
      .then(() => ensureDokumenteLoaded({ force: true }));
  }

  const requestedUid = u;
  const loadToken = ++dokumenteLoadToken;
  dokumenteLoadUid = requestedUid;
  dokumenteLoadPromise = loadDokumente()
    .then((docs) => {
      if (loadToken === dokumenteLoadToken && auth.currentUser?.uid === requestedUid) {
        dokumenteCache = docs;
        dokumenteCacheUid = requestedUid;
        emitDokumente(docs);
      }
      return docs;
    })
    .catch((err) => {
      if (loadToken === dokumenteLoadToken) {
        console.error('[Firestore] Dokumente laden fehlgeschlagen:', err.message, err.code);
        emitDokumenteError(err);
      }
      throw err;
    })
    .finally(() => {
      if (loadToken === dokumenteLoadToken) {
        dokumenteLoadPromise = null;
        dokumenteLoadUid = null;
      }
    });

  return dokumenteLoadPromise;
}

function scheduleDokumenteReload() {
  if (dokumenteReloadTimer) clearTimeout(dokumenteReloadTimer);
  dokumenteReloadTimer = setTimeout(() => {
    dokumenteReloadTimer = null;
    if (dokumenteSubscribers.size === 0) return;
    ensureDokumenteLoaded({ force: true }).catch(() => {});
  }, 250);
}

function handleDokumenteSignal(source) {
  if (source && source === currentTabId()) return;
  dokumenteCache = null;
  scheduleDokumenteReload();
}

function setupDokumenteSignals() {
  if (dokumenteSignalsReady || typeof window === 'undefined') return;
  dokumenteSignalsReady = true;

  window.addEventListener(DOKUMENTE_SIGNAL, (event) => handleDokumenteSignal(event.detail?.source));
  window.addEventListener('storage', (event) => {
    if (event.key !== DOKUMENTE_SIGNAL || !event.newValue) return;
    try {
      const payload = JSON.parse(event.newValue);
      handleDokumenteSignal(payload.source);
    } catch {
      handleDokumenteSignal();
    }
  });

  if ('BroadcastChannel' in window) {
    dokumenteChannel = new BroadcastChannel(DOKUMENTE_CHANNEL);
    dokumenteChannel.onmessage = (event) => handleDokumenteSignal(event.data?.source);
  }
}

function signalDokumenteChanged({ broadcast = true } = {}) {
  dokumenteCache = null;
  scheduleDokumenteReload();

  if (typeof window === 'undefined') return;
  const payload = { source: currentTabId(), at: Date.now() };
  window.dispatchEvent(new CustomEvent(DOKUMENTE_SIGNAL, { detail: payload }));

  if (!broadcast) return;
  try { dokumenteChannel?.postMessage(payload); } catch { /* ignore */ }
  try { localStorage.setItem(DOKUMENTE_SIGNAL, JSON.stringify(payload)); } catch { /* ignore */ }
}

/**
 * Listen to cached document updates for the current user.
 * Loads Firestore once initially and again only after local/cross-tab mutations.
 */
export function subscribeDokumente(onChange, onError) {
  try {
    setupDokumenteSignals();
    const u = uid();
    if (dokumenteCacheUid && dokumenteCacheUid !== u) {
      dokumenteCache = null;
      dokumenteCacheUid = null;
    }

    const subscriber = { onChange, onError };
    dokumenteSubscribers.add(subscriber);
    ensureDokumenteLoaded().catch(() => {});
    return () => { dokumenteSubscribers.delete(subscriber); };
  } catch (e) {
    onError?.(e);
    return () => {};
  }
}

/**
 * Upload a single file into a category via the backend API.
 * The backend enforces storage limits before accepting the file.
 */
export async function uploadDokument(kategorie, file) {
  const token = await _idToken();
  const form = new FormData();
  form.append('file', file);
  form.append('kategorie', kategorie);

  const resp = await fetch(`${API_URL}/dokumente/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  if (!resp.ok) {
    let detail = 'Upload fehlgeschlagen';
    try { detail = (await resp.json()).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  const result = await resp.json();
  signalDokumenteChanged();
  return result;
}

/**
 * Delete a document — both Firestore meta and Storage blob.
 */
export async function deleteDokument(docMeta) {
  if (docMeta.storagePath) {
    try { await deleteObject(ref(storage, docMeta.storagePath)); }
    catch (e) { /* file might already be gone — ignore */ }
  }
  await deleteDoc(doc(db, COL, docMeta.id));
  signalDokumenteChanged();
}

/**
 * Upload a cropped photo blob via the backend API.
 * Returns { url, storagePath }.
 */
export async function uploadCroppedFoto(blob, originalFilename) {
  const filename = originalFilename || 'Bewerbungsfoto.jpg';
  const file = new File([blob], filename, { type: 'image/jpeg' });
  const result = await uploadDokument('fotos', file);
  return { url: result.url, storagePath: result.storagePath };
}

/**
 * Toggle the favorit flag on a document.
 */
export async function toggleFavorit(docId, isFavorit) {
  const ref = doc(db, 'dokumente', docId);
  await updateDoc(ref, { favorit: isFavorit });
  signalDokumenteChanged();
}

/**
 * Create a named folder for a given kategorie (stored per-user in Firestore).
 */
export async function createOrdner(kategorie, name) {
  const u = uid();
  await setDoc(
    doc(db, 'users', u, 'settings', 'dokumente'),
    { [`ordner_${kategorie}`]: arrayUnion(name) },
    { merge: true },
  );
}

/**
 * Subscribe to the folder list for a given kategorie.
 */
export function subscribeOrdner(kategorie, onChange) {
  const u = uid();
  return onSnapshot(
    doc(db, 'users', u, 'settings', 'dokumente'),
    (snap) => { const d = snap.data() || {}; onChange(d[`ordner_${kategorie}`] || []); },
    () => onChange([]),
  );
}

/**
 * Assign (or remove) a folder on an uploaded document.
 */
export async function setDokumentOrdner(docId, ordner) {
  await updateDoc(doc(db, COL, docId), { ordner: ordner || null });
  signalDokumenteChanged();
}

/**
 * Rename a folder. Updates the folder list and all documents inside it.
 */
export async function renameOrdner(kategorie, oldName, newName, affectedDocIds = []) {
  const u = uid();
  const settingsRef = doc(db, 'users', u, 'settings', 'dokumente');
  await updateDoc(settingsRef, { [`ordner_${kategorie}`]: arrayRemove(oldName) });
  await updateDoc(settingsRef, { [`ordner_${kategorie}`]: arrayUnion(newName) });
  await Promise.all(affectedDocIds.map(id => updateDoc(doc(db, COL, id), { ordner: newName })));
  signalDokumenteChanged();
}

/**
 * Delete a folder from the list. Pass docIds of files currently in that folder
 * so they get moved back to root automatically.
 */
export async function deleteOrdner(kategorie, name, affectedDocIds = []) {
  const u = uid();
  await updateDoc(
    doc(db, 'users', u, 'settings', 'dokumente'),
    { [`ordner_${kategorie}`]: arrayRemove(name) },
  );
  await Promise.all(affectedDocIds.map(id => updateDoc(doc(db, COL, id), { ordner: null })));
  signalDokumenteChanged();
}

/**
 * Fetch a Firebase Storage image via backend proxy (avoids canvas CORS taint).
 * Only needed for production Firebase Storage URLs, not for emulator URLs.
 * Returns a blob URL that can be drawn to canvas.
 */
export async function proxyImageAlsBlobUrl(storageUrl) {
  const token = await _idToken();
  const resp = await fetch(
    `${API_URL}/fotos/proxy-image?url=${encodeURIComponent(storageUrl)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!resp.ok) throw new Error('Bild konnte nicht geladen werden');
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}
